import * as React from 'react';

import { TextMessage } from '../../../../components/TextMessage';
import { useMessageContext } from '../../../../utils/messageContext';
import { useTextMessageContent } from './hooks/useTextMessageContent';
import { trackClickMessage } from '../../tracking';
import { UserNameContainer } from '../UserNameContainer/UserNameContainer';
import { MessageReplyQuoteContainer } from '../../../MessageReplyQuoteContainer';
import { ChatMessageMetaContainer } from '../../containers/ChatMessageMetaContainer';
import { ErrorSignContainer } from '../ErrorSignContainer';
import { ErrorTextContainer } from '../ErrorTextContainer';
import { ActionButtonsContainer } from '../ActionButtonsContainer';
import { useMessageImages } from './hooks/useMessageImages';
import { ImageWithTextMessageLayout } from '../../../../components/ImageWithTextMessageLayout';
import { ImageMessageLayout } from '../../../../components/ImageMessageLayout';
import { TextMessageLayout } from '../../../../components/TextMessageLayout/TextMessageLayout';

export const TextAndImagesMessageContainer: React.FC = () => {
  const {
    message: { isOutcoming, communicationId, offers, isDeleted },
    messageType,
    isLastInChain,
    userColor,
    highlighted,
    onHighlightEnd,
    chatId,
    messageId,
    messageRef,
  } = useMessageContext();

  const images = useMessageImages();

  const textContent = useTextMessageContent();

  const onClickMessage = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if ((e.target as HTMLElement).tagName === 'A') {
        const anchor = e.target as HTMLAnchorElement;
        const messageType = anchor.href.includes('tel') ? 'phone' : 'link';
        const clickText = anchor.dataset.clickText;

        trackClickMessage({
          chatId,
          messageId,
          messageType,
          offerId: offers?.[0]?.realtyOfferId,
          communicationId,
          clickText,
        });
      }
    },
    // oxlint-disable-next-line react/exhaustive-deps
    [chatId, offers],
  );

  if (isDeleted || offers?.length) {
    return null;
  }

  if (textContent && images) {
    return (
      <ImageWithTextMessageLayout
        errorSign={<ErrorSignContainer />}
        errorText={<ErrorTextContainer />}
        messageType={messageType}
        isLastMessageInChain={isLastInChain}
        imageMessage={images}
        textMessage={
          <TextMessage
            text={textContent}
            onClick={onClickMessage}
            textColor={isOutcoming ? 'text-inverted-default' : 'text-primary-default'}
          />
        }
        messageMeta={<ChatMessageMetaContainer />}
        actionButtons={<ActionButtonsContainer />}
        userColor={userColor}
        replyQuote={<MessageReplyQuoteContainer />}
        highlighted={highlighted}
        onHighlightEnd={onHighlightEnd}
        ref={messageRef}
      />
    );
  }

  if (textContent) {
    return (
      <TextMessageLayout
        errorSign={<ErrorSignContainer />}
        errorText={<ErrorTextContainer />}
        messageType={messageType}
        isLastMessageInChain={isLastInChain}
        userName={<UserNameContainer />}
        textMessage={
          <TextMessage
            text={textContent}
            onClick={onClickMessage}
            textColor={isOutcoming ? 'text-inverted-default' : 'text-primary-default'}
          />
        }
        messageMeta={<ChatMessageMetaContainer />}
        actionButtons={<ActionButtonsContainer />}
        userColor={userColor}
        replyQuote={<MessageReplyQuoteContainer />}
        highlighted={highlighted}
        onHighlightEnd={onHighlightEnd}
        ref={messageRef}
      />
    );
  }

  if (images) {
    return (
      <ImageMessageLayout
        errorSign={<ErrorSignContainer />}
        errorText={<ErrorTextContainer />}
        messageType={messageType}
        isLastMessageInChain={isLastInChain}
        imageMessage={images}
        messageMeta={<ChatMessageMetaContainer color="text-inverted-default" />}
        userColor={userColor}
        replyQuote={<MessageReplyQuoteContainer />}
        highlighted={highlighted}
        onHighlightEnd={onHighlightEnd}
        ref={messageRef}
      />
    );
  }

  return null;
};
